import React, { useState } from 'react';
import { useChat } from '../context/ChatContext';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Search,
  PiggyBank,
  Ticket,
  Loader2,
  CheckCircle2,
  AlertCircle,
  History,
  Sparkles,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatINR(n) {
  if (n === null || n === undefined || n === '') return '-';
  const num = Number(n);
  if (!Number.isFinite(num)) return '-';
  return `₹${num.toLocaleString('en-IN')}`;
}

function StatusPill({ kind, children }) {
  const palette = {
    success: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    error: 'bg-red-500/15 text-red-400 border-red-500/30',
    info: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  };
  return (
    <div className={`text-sm px-3 py-2 rounded-md border ${palette[kind] || palette.info}`}>
      {children}
    </div>
  );
}

export default function CustomerTools() {
  const { selectedBrand } = useChat();
  const brandId = selectedBrand?.brand_id;
  const brandName = selectedBrand?.name || 'this brand';

  const [mobile, setMobile] = useState('');
  const [lookup, setLookup] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupErr, setLookupErr] = useState('');

  // Points form
  const [points, setPoints] = useState('');
  const [reason, setReason] = useState('admin_adjustment');
  const [creditingMsg, setCreditingMsg] = useState(null);
  const [crediting, setCrediting] = useState(false);

  // Coupon form
  const [couponForm, setCouponForm] = useState({
    coupon_code: '',
    name: 'Personal Offer',
    discount_type: 'flat',
    discount_value: '',
    min_bill_amount: '',
    max_discount_amount: '',
    valid_until: '',
    offer_instruction: '',
  });
  const [assigningMsg, setAssigningMsg] = useState(null);
  const [assigning, setAssigning] = useState(false);

  const tenDigit = (mobile || '').replace(/\D/g, '').slice(-10);
  const isValidMobile = tenDigit.length === 10;

  const doLookup = async () => {
    if (!brandId || !isValidMobile) return;
    setLookingUp(true);
    setLookupErr('');
    setLookup(null);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/customer-lookup/${brandId}?mobile=${tenDigit}`,
        { credentials: 'include' }
      );
      const data = await res.json();
      if (!res.ok || data?.success === false) {
        setLookupErr(data?.detail || data?.message || 'Lookup failed');
      } else {
        setLookup(data);
      }
    } catch (e) {
      setLookupErr(String(e.message || e));
    } finally {
      setLookingUp(false);
    }
  };

  const refreshLookup = () => {
    if (lookup) doLookup();
  };

  const creditPoints = async () => {
    if (!brandId || !isValidMobile) return;
    const pts = parseInt(points, 10);
    if (!Number.isFinite(pts) || pts === 0) {
      setCreditingMsg({ kind: 'error', text: 'Enter a non-zero point value (positive to credit, negative to debit).' });
      return;
    }
    setCrediting(true);
    setCreditingMsg(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/credit-points/${brandId}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: tenDigit, points: pts, reason }),
      });
      const data = await res.json();
      if (!res.ok || data?.success === false) {
        setCreditingMsg({ kind: 'error', text: data?.detail || data?.message || 'Failed' });
      } else {
        setCreditingMsg({
          kind: 'success',
          text: `${pts > 0 ? 'Credited' : 'Debited'} ${Math.abs(pts)} pts. New balance: ${data.new_balance}`,
        });
        setPoints('');
        refreshLookup();
      }
    } catch (e) {
      setCreditingMsg({ kind: 'error', text: String(e.message || e) });
    } finally {
      setCrediting(false);
    }
  };

  const assignCoupon = async () => {
    if (!brandId || !isValidMobile) return;
    if (!couponForm.discount_value || Number(couponForm.discount_value) <= 0) {
      setAssigningMsg({ kind: 'error', text: 'Discount value must be greater than 0.' });
      return;
    }
    setAssigning(true);
    setAssigningMsg(null);
    try {
      const payload = {
        mobile: tenDigit,
        coupon_code: (couponForm.coupon_code || '').trim().toUpperCase(),
        name: couponForm.name || 'Personal Offer',
        discount_type: couponForm.discount_type,
        discount_value: Number(couponForm.discount_value || 0),
        min_bill_amount: Number(couponForm.min_bill_amount || 0),
        max_discount_amount: Number(couponForm.max_discount_amount || 0),
        valid_until: couponForm.valid_until || '',
        offer_instruction: couponForm.offer_instruction || '',
        max_redemptions: 1,
      };
      const res = await fetch(`${API_URL}/api/admin/assign-coupon/${brandId}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || data?.success === false) {
        setAssigningMsg({ kind: 'error', text: data?.detail || data?.message || 'Failed to assign coupon' });
      } else {
        setAssigningMsg({
          kind: 'success',
          text: `Coupon ${data.coupon.coupon_code} assigned to ${tenDigit}.`,
        });
        setCouponForm({ ...couponForm, coupon_code: '', discount_value: '', valid_until: '', offer_instruction: '' });
        refreshLookup();
      }
    } catch (e) {
      setAssigningMsg({ kind: 'error', text: String(e.message || e) });
    } finally {
      setAssigning(false);
    }
  };

  if (!brandId) {
    return (
      <div className="p-6 text-muted-foreground" data-testid="customer-tools-empty">
        Select a brand from the sidebar to use Points & Coupons tools.
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-6xl" data-testid="customer-tools-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Sparkles className="w-6 h-6" /> Points &amp; Coupons — {brandName}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manually credit points or assign a personal coupon to any customer mobile number.
        </p>
      </div>

      {/* Lookup */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Search className="w-4 h-4" /> Customer Lookup
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              data-testid="customer-tools-mobile-input"
              placeholder="10-digit mobile number"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && doLookup()}
              className="sm:max-w-xs"
            />
            <Button
              onClick={doLookup}
              disabled={!isValidMobile || lookingUp}
              data-testid="customer-tools-lookup-btn"
            >
              {lookingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              <span className="ml-2">Lookup</span>
            </Button>
          </div>

          {lookupErr && (
            <StatusPill kind="error">
              <span className="inline-flex items-center gap-2">
                <AlertCircle className="w-4 h-4" /> {lookupErr}
              </span>
            </StatusPill>
          )}

          {lookup && (
            <div className="grid sm:grid-cols-2 gap-4 pt-2" data-testid="customer-tools-lookup-result">
              <div className="space-y-1 text-sm">
                <div className="text-muted-foreground text-xs uppercase">Profile</div>
                {lookup.customer ? (
                  <>
                    <div><span className="text-muted-foreground">Name:</span> {lookup.customer.name || '-'}</div>
                    <div><span className="text-muted-foreground">Mobile:</span> {lookup.mobile}</div>
                    <div><span className="text-muted-foreground">Email:</span> {lookup.customer.email || '-'}</div>
                    <div><span className="text-muted-foreground">Tier:</span> {lookup.customer.tier || '-'}</div>
                    <div><span className="text-muted-foreground">Total Spend:</span> {formatINR(lookup.customer.total_spend)}</div>
                    <div><span className="text-muted-foreground">Txn Count:</span> {lookup.customer.transaction_count ?? '-'}</div>
                  </>
                ) : (
                  <div className="text-amber-400 text-sm">
                    No customer found. They&apos;ll be auto-registered when you credit points or assign a coupon.
                  </div>
                )}
              </div>
              <div className="space-y-1 text-sm">
                <div className="text-muted-foreground text-xs uppercase">Points</div>
                <div className="text-3xl font-bold" data-testid="customer-tools-balance">{lookup.ledger_balance.toLocaleString('en-IN')}</div>
                <div className="text-xs text-muted-foreground">From point ledger (source of truth)</div>
                {lookup.cached_balance !== lookup.ledger_balance && (
                  <div className="text-xs text-amber-400">Cached balance: {lookup.cached_balance}</div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {lookup && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Credit Points */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <PiggyBank className="w-4 h-4" /> Credit / Debit Points
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-muted-foreground">Points (negative = debit)</label>
                  <Input
                    data-testid="customer-tools-points-input"
                    type="number"
                    placeholder="500"
                    value={points}
                    onChange={(e) => setPoints(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Reason</label>
                  <Input
                    data-testid="customer-tools-reason-input"
                    placeholder="bonus / goodwill / fix"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </div>
              </div>
              <Button
                onClick={creditPoints}
                disabled={crediting || !points}
                className="w-full"
                data-testid="customer-tools-credit-btn"
              >
                {crediting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PiggyBank className="w-4 h-4 mr-2" />}
                Apply
              </Button>
              {creditingMsg && (
                <StatusPill kind={creditingMsg.kind}>
                  <span className="inline-flex items-center gap-2">
                    {creditingMsg.kind === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                    {creditingMsg.text}
                  </span>
                </StatusPill>
              )}
            </CardContent>
          </Card>

          {/* Assign Coupon */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Ticket className="w-4 h-4" /> Assign Coupon
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-muted-foreground">Code (optional)</label>
                  <Input
                    data-testid="customer-tools-coupon-code"
                    placeholder="HARDIK500"
                    value={couponForm.coupon_code}
                    onChange={(e) => setCouponForm({ ...couponForm, coupon_code: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Name</label>
                  <Input
                    data-testid="customer-tools-coupon-name"
                    placeholder="Personal Offer"
                    value={couponForm.name}
                    onChange={(e) => setCouponForm({ ...couponForm, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Discount Type</label>
                  <select
                    data-testid="customer-tools-coupon-disc-type"
                    className="w-full bg-background border border-input rounded-md px-3 py-2 text-sm h-10"
                    value={couponForm.discount_type}
                    onChange={(e) => setCouponForm({ ...couponForm, discount_type: e.target.value })}
                  >
                    <option value="flat">Flat ₹</option>
                    <option value="percent">Percent %</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Discount Value</label>
                  <Input
                    data-testid="customer-tools-coupon-disc-value"
                    type="number"
                    placeholder="500"
                    value={couponForm.discount_value}
                    onChange={(e) => setCouponForm({ ...couponForm, discount_value: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Min Bill</label>
                  <Input
                    data-testid="customer-tools-coupon-min-bill"
                    type="number"
                    placeholder="0"
                    value={couponForm.min_bill_amount}
                    onChange={(e) => setCouponForm({ ...couponForm, min_bill_amount: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Max Discount</label>
                  <Input
                    data-testid="customer-tools-coupon-max-disc"
                    type="number"
                    placeholder="0 = no cap"
                    value={couponForm.max_discount_amount}
                    onChange={(e) => setCouponForm({ ...couponForm, max_discount_amount: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-muted-foreground">Valid Until (YYYY-MM-DD, optional)</label>
                  <Input
                    data-testid="customer-tools-coupon-valid-until"
                    placeholder="2026-12-31"
                    value={couponForm.valid_until}
                    onChange={(e) => setCouponForm({ ...couponForm, valid_until: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-muted-foreground">Offer instruction (shown at POS)</label>
                  <Input
                    data-testid="customer-tools-coupon-instruction"
                    placeholder="Apply at checkout"
                    value={couponForm.offer_instruction}
                    onChange={(e) => setCouponForm({ ...couponForm, offer_instruction: e.target.value })}
                  />
                </div>
              </div>
              <Button
                onClick={assignCoupon}
                disabled={assigning}
                className="w-full"
                data-testid="customer-tools-assign-coupon-btn"
              >
                {assigning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Ticket className="w-4 h-4 mr-2" />}
                Assign Coupon
              </Button>
              {assigningMsg && (
                <StatusPill kind={assigningMsg.kind}>
                  <span className="inline-flex items-center gap-2">
                    {assigningMsg.kind === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                    {assigningMsg.text}
                  </span>
                </StatusPill>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recent ledger + active coupons */}
      {lookup && (
        <div className="grid lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <History className="w-4 h-4" /> Recent Ledger (last 10)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {lookup.recent_ledger.length === 0 ? (
                <div className="text-sm text-muted-foreground">No ledger entries yet.</div>
              ) : (
                <div className="text-xs space-y-1" data-testid="customer-tools-ledger-list">
                  {lookup.recent_ledger.map((l, i) => (
                    <div key={i} className="flex items-center justify-between border-b border-border/40 py-1">
                      <div>
                        <div className="font-medium">{l.type}</div>
                        <div className="text-muted-foreground">{l.description || '-'}</div>
                      </div>
                      <div className="text-right">
                        <div className={l.points >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                          {l.points >= 0 ? '+' : ''}{l.points}
                        </div>
                        <div className="text-muted-foreground">
                          {(l.created_at || '').slice(0, 10)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Ticket className="w-4 h-4" /> Active Coupons ({lookup.coupons.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {lookup.coupons.length === 0 ? (
                <div className="text-sm text-muted-foreground">No active coupons available.</div>
              ) : (
                <div className="text-xs space-y-2" data-testid="customer-tools-coupons-list">
                  {lookup.coupons.map((c, i) => (
                    <div key={i} className="border border-border/60 rounded-md p-2">
                      <div className="flex justify-between">
                        <div className="font-bold">{c.coupon_code}</div>
                        <div className="text-muted-foreground">{c.type}</div>
                      </div>
                      <div className="text-muted-foreground">{c.name || '-'}</div>
                      <div>
                        {c.discount_type === 'percent'
                          ? `${c.discount_value}% off`
                          : `₹${c.discount_value} off`}
                        {c.min_bill_amount ? ` · Min ₹${c.min_bill_amount}` : ''}
                        {c.valid_until ? ` · until ${c.valid_until}` : ''}
                      </div>
                      {c.assigned_mobile && (
                        <div className="text-amber-400">Assigned: {c.assigned_mobile}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
